import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
} from "@tanstack/react-table";

/**
 * Paging handled by the caller, for sets too large to send to the browser.
 *
 * `data` then holds one page rather than everything, and `totalRows` is the
 * size of the whole set — without it the pager would describe the page as if
 * it were the lot, which is exactly the bug this exists to fix.
 */
export interface ServerPagination {
  totalRows: number;
  pageIndex: number;
  pageSize: number;
  onPageChange: (pageIndex: number) => void;
  /** Dims the table and locks the pager while a page is in flight. */
  loading?: boolean;
}

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  onRowClick?: (row: T) => void;
  initialPageSize?: number;
  /** Shown instead of the generic message when there is nothing to display. */
  emptyMessage?: string;
  /** Omit for the usual client-side paging over the full `data` array. */
  serverPagination?: ServerPagination;
}

export default function DataTable<T>({
  data,
  columns,
  onRowClick,
  initialPageSize = 25,
  emptyMessage = "No records found.",
  serverPagination,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const server = serverPagination;

  const table = useReactTable({
    data,
    columns,
    // Sorting a server-paged table would reorder the rows on screen and leave
    // the other pages where they were, which reads as a sort of everything and
    // is not one. Columns are made unsortable below instead.
    state: server ? {} : { sorting },
    onSortingChange: server ? undefined : setSorting,
    // Turned off rather than left to do nothing: the header would still look
    // clickable and clicking it would be ignored.
    enableSorting: !server,
    getCoreRowModel: getCoreRowModel(),
    ...(server
      ? {
          manualPagination: true as const,
          pageCount: Math.max(1, Math.ceil(server.totalRows / server.pageSize)),
        }
      : {
          getSortedRowModel: getSortedRowModel(),
          getPaginationRowModel: getPaginationRowModel(),
        }),
    initialState: { pagination: { pageSize: server?.pageSize ?? initialPageSize } },
  });

  const pageIndex = server ? server.pageIndex : table.getState().pagination.pageIndex;
  const pageSize = server ? server.pageSize : table.getState().pagination.pageSize;
  const totalRows = server ? server.totalRows : data.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const startRow = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const endRow = Math.min(startRow + pageSize - 1, totalRows);

  const canPrevious = server ? pageIndex > 0 && !server.loading : table.getCanPreviousPage();
  const canNext = server
    ? pageIndex + 1 < pageCount && !server.loading
    : table.getCanNextPage();

  const goTo = (index: number) => {
    if (server) server.onPageChange(Math.min(Math.max(index, 0), pageCount - 1));
    else table.setPageIndex(index);
  };

  const pagerButton =
    "px-2 py-1 rounded border border-edge disabled:opacity-40 hover:bg-surface-hover";

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto rounded-xl border border-edge shadow-sm">
        <table className="min-w-full divide-y divide-edge text-sm">
          <thead className="bg-surface-sunken">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortDirection = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      // aria-sort lets a screen reader announce the current sort
                      // state, which the icon alone does not convey.
                      aria-sort={
                        !canSort
                          ? undefined
                          : sortDirection === "asc"
                          ? "ascending"
                          : sortDirection === "desc"
                          ? "descending"
                          : "none"
                      }
                      className={`px-4 py-3 text-left text-xs font-semibold text-content uppercase tracking-wider select-none ${
                        canSort ? "cursor-pointer hover:text-accent" : ""
                      }`}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span className="flex items-center gap-1">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span className="text-content-subtle" aria-hidden="true">
                            {sortDirection === "asc" ? (
                              <svg className="w-3 h-3 text-accent" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                              </svg>
                            ) : sortDirection === "desc" ? (
                              <svg className="w-3 h-3 text-accent" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            )}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody
            className={`bg-surface-raised divide-y divide-edge-subtle transition-opacity ${
              server?.loading ? "opacity-50" : ""
            }`}
          >
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-content-subtle"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                  className={`transition-colors duration-75 ${
                    onRowClick ? "cursor-pointer hover:bg-accent-subtle" : "hover:bg-surface-hover"
                  }`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3 text-content whitespace-nowrap">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalRows > pageSize && (
        <div className="flex items-center justify-between text-sm text-content-muted px-1">
          <span>
            Showing {startRow}–{endRow} of {totalRows} rows
            {server?.loading && <span className="ml-2 italic">loading…</span>}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="First page"
              onClick={() => goTo(0)}
              disabled={!canPrevious}
              className={pagerButton}
            >
              «
            </button>
            <button
              type="button"
              aria-label="Previous page"
              onClick={() => goTo(pageIndex - 1)}
              disabled={!canPrevious}
              className={pagerButton}
            >
              ‹
            </button>
            <span className="px-3">
              Page {pageIndex + 1} / {pageCount}
            </span>
            <button
              type="button"
              aria-label="Next page"
              onClick={() => goTo(pageIndex + 1)}
              disabled={!canNext}
              className={pagerButton}
            >
              ›
            </button>
            <button
              type="button"
              aria-label="Last page"
              onClick={() => goTo(pageCount - 1)}
              disabled={!canNext}
              className={pagerButton}
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
